import React, { useState, useEffect } from "react";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Search,
  Hash,
  Phone,
  Mail,
  IndianRupee,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Lock,
  LogOut,
  Eye,
  EyeOff,
} from "lucide-react";
import API_BASE_URL from "./ApiConfig";

const STREAMS = [
  "All",
  "Full Stack Website Development",
  "Android Development",
  "Python Development",
  "Website with IoT",
  "Android App Development with IoT",
];

const FEE_AMOUNT = 200;
const ADMIN_PASSWORD = "nandini123#";

export default function AdminSssStudentView() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword, setShowPassword] = useState(false); // Toggle state for password
  
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  
  const [updatingId, setUpdatingId] = useState(null);

  // Check localStorage on mount
  useEffect(() => {
    const authFlag = localStorage.getItem("sss_admin_auth");
    if (authFlag === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStudents();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      localStorage.setItem("sss_admin_auth", "true");
      setIsAuthenticated(true);
      toast.success("Access Granted");
    } else {
      toast.error("Incorrect password");
      setPasswordInput("");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("sss_admin_auth");
    setIsAuthenticated(false);
    setPasswordInput("");
    setShowPassword(false);
    setStudents([]); // Clear data from memory
    toast.info("Logged out successfully");
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/students`);
      const result = await response.json();

      if (result.success) {
        setStudents(result.data);
      } else {
        toast.error(result.message || "Failed to fetch students.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Could not connect to the server.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (id, method, currentMethod) => {
    if (currentMethod === method && students.find(s => s.id === id)?.paymentStatus === "paid") {
      return;
    }

    setUpdatingId(`${id}-${method}`);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/students/${id}/pay`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ paymentMethod: method }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`Payment marked as ${method.toUpperCase()}!`);
        
        setStudents((prevStudents) =>
          prevStudents.map((student) =>
            student.id === id
              ? { ...student, paymentStatus: "paid", paymentMethod: method }
              : student
          )
        );
      } else {
        toast.error(result.message || "Failed to update payment.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Could not connect to the server.");
    } finally {
      setUpdatingId(null);
    }
  };

  // Filter logic for Cards
  const filteredStudents = students.filter((student) => {
    const isPaid = student.paymentStatus === "paid";

    // 1. Tab Filter
    if (activeTab === "Paid" && !isPaid) return false;
    if (activeTab !== "Paid" && activeTab !== "All" && student.stream !== activeTab) return false;
    
    // "All" tab specifically shows ONLY Pending students
    if (activeTab === "All" && isPaid) return false; 

    // 2. Search Filter
    const term = searchTerm.toLowerCase();
    const name = (student.name || "").toLowerCase();
    const regNo = (student.regNo || "").toLowerCase();
    const phone = (student.phone || "").toLowerCase();
    const refNo = `AKSD-${(student.phone || "").slice(-4)}`.toLowerCase();

    return name.includes(term) || regNo.includes(term) || phone.includes(term) || refNo.includes(term);
  });

  // Calculate Stats
  const upiStudentsCount = students.filter((s) => s.paymentStatus === "paid" && s.paymentMethod === "upi").length;
  const cashStudentsCount = students.filter((s) => s.paymentStatus === "paid" && s.paymentMethod === "cash").length;
  
  const upiTotal = upiStudentsCount * FEE_AMOUNT;
  const cashTotal = cashStudentsCount * FEE_AMOUNT;

  // Helper function to get tab counts
  const getTabCount = (tab) => {
    if (tab === "All") {
      return students.filter(s => s.paymentStatus !== "paid").length;
    }
    if (tab === "Paid") {
      return students.filter(s => s.paymentStatus === "paid").length;
    }
    return students.filter(s => s.stream === tab).length;
  };

  // --- SECURITY SCREEN ---
  if (!isAuthenticated) {
    return (
      <div className="admin-page d-flex align-items-center justify-content-center">
        <style>{css}</style>
        <ToastContainer position="top-center" autoClose={3000} theme="dark" />
        <div className="admin-login-box">
          <div className="admin-login-icon">
            <Lock size={32} />
          </div>
          <h2>Admin Access</h2>
          <p>Please enter the password to view student registrations.</p>
          <form onSubmit={handleLogin}>
            <div className="admin-password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                autoFocus
              />
              <button
                type="button"
                className="admin-eye-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <button type="submit" className="admin-btn admin-btn-primary w-100 mt-3">
              Unlock Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- MAIN DASHBOARD ---
  return (
    <div className="admin-page">
      <style>{css}</style>
      <ToastContainer position="top-center" autoClose={3000} theme="dark" />

      {/* Header */}
      <header className="admin-header">
        <Container fluid className="px-4">
          <div className="admin-header-content">
            <div>
              <h1>Admin Student Form View</h1>
              <p>Sri Sathya Sai Institute for Women - Registered Students</p>
            </div>
            
            <div className="admin-header-actions">
              <div className="admin-compact-stats">
                <div className="admin-stat-item">
                  <span className="admin-stat-dot upi-dot" />
                  <span className="admin-stat-text">
                    UPI: <strong>₹{upiTotal}</strong> <span className="admin-stat-count">({upiStudentsCount})</span>
                  </span>
                </div>
                <div className="admin-stat-item">
                  <span className="admin-stat-dot cash-dot" />
                  <span className="admin-stat-text">
                    Cash: <strong>₹{cashTotal}</strong> <span className="admin-stat-count">({cashStudentsCount})</span>
                  </span>
                </div>
              </div>
              
              <div className="admin-action-buttons">
                <button type="button" className="admin-btn admin-btn-outline" onClick={fetchStudents} disabled={loading}>
                  <RefreshCw size={16} className={loading ? "spin" : ""} /> Refresh
                </button>
                <button type="button" className="admin-btn admin-btn-logout" onClick={handleLogout}>
                  <LogOut size={16} /> Logout
                </button>
              </div>
            </div>
          </div>

          {/* Filter Tabs with Counts */}
          <div className="admin-tabs-container">
            {STREAMS.map((stream) => (
              <button
                key={stream}
                className={`admin-tab ${activeTab === stream ? "active" : ""}`}
                onClick={() => setActiveTab(stream)}
              >
                {stream} <span className="admin-tab-count">({getTabCount(stream)})</span>
              </button>
            ))}
            <button
              className={`admin-tab ${activeTab === "Paid" ? "active" : ""}`}
              onClick={() => setActiveTab("Paid")}
            >
              Paid <span className="admin-tab-count">({getTabCount("Paid")})</span>
            </button>
          </div>
        </Container>
      </header>

      <Container fluid className="px-4 admin-main">
        
        {/* Search Bar */}
        <div className="admin-search-box">
          <Search size={20} className="admin-search-icon" />
          <input
            type="text"
            placeholder="Search by reference number, student name, or phone number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-search-input"
          />
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="admin-loading">
            <Spinner animation="border" variant="primary" />
            <p>Loading students data...</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="admin-empty">
            <AlertCircle size={40} />
            <h3>No students found</h3>
            <p>
              {searchTerm || activeTab !== "All"
                ? "No matches found for your current filters. Try adjusting them."
                : "No pending registrations have been submitted yet."}
            </p>
          </div>
        ) : (
          <Row className="g-4">
            {filteredStudents.map((student) => {
              const isPaid = student.paymentStatus === "paid";
              const paidMethod = student.paymentMethod;
              const refNo = `AKSD-${(student.phone || "").slice(-4)}`;

              const isUpiLoading = updatingId === `${student.id}-upi`;
              const isCashLoading = updatingId === `${student.id}-cash`;

              return (
                <Col xs={12} md={6} lg={4} xl={3} key={student.id}>
                  <div className="admin-card">
                    <div className="admin-card-top">
                      <div className="admin-photo">
                        <img 
                          src={student.photoUrl} 
                          alt={student.name} 
                          loading="lazy" 
                          className="admin-img"
                        />
                      </div>
                      <div className="admin-card-basic">
                        <h3 title={student.name}>{student.name}</h3>
                        <span className="admin-ref">{refNo}</span>
                        
                        <div className="admin-info-row" title={student.regNo}>
                          <Hash size={14} />
                          <span>{student.regNo}</span>
                        </div>
                        <div className="admin-info-row" title={`+91 ${student.phone}`}>
                          <Phone size={14} />
                          <span>+91 {student.phone}</span>
                        </div>
                        <div className="admin-info-row" title={student.email}>
                          <Mail size={14} />
                          <span>{student.email}</span>
                        </div>
                      </div>
                    </div>

                    <div className="admin-divider" />

                    <div className="admin-course-details">
                      <div className="admin-detail-row">
                        <span className="admin-label">Semester</span>
                        <span className="admin-value">{student.semester}</span>
                      </div>
                      <div className="admin-detail-row">
                        <span className="admin-label">Stream</span>
                        <span className="admin-value admin-stream-text" title={student.stream}>
                          {student.stream}
                        </span>
                      </div>
                      <div className="admin-detail-row">
                        <span className="admin-label">Level</span>
                        <span className="admin-value">{student.level} / 10</span>
                      </div>
                      <div className="admin-detail-row">
                        <span className="admin-label">Registered</span>
                        <span className="admin-value">
                          {new Date(student.registeredAt).toLocaleDateString("en-IN")}
                        </span>
                      </div>
                    </div>

                    <div className="admin-payment-section">
                      <div className="admin-payment-title">
                        <IndianRupee size={16} /> Registration Fee Status
                      </div>
                      
                      {isPaid && (
                        <div className="admin-paid-indicator">
                          <CheckCircle2 size={16} />
                          <span>Paid: {paidMethod?.toUpperCase()}</span>
                        </div>
                      )}

                      <div className="admin-payment-actions">
                        <button
                          type="button"
                          className={`admin-btn admin-btn-upi ${isPaid && paidMethod === "upi" ? "active" : ""}`}
                          onClick={() => handlePayment(student.id, "upi", paidMethod)}
                          disabled={updatingId !== null || (isPaid && paidMethod === "upi")}
                        >
                          {isUpiLoading ? <Spinner size="sm" animation="border" /> : (isPaid && paidMethod === "upi" ? "UPI Selected" : "Paid via UPI")}
                        </button>
                        <button
                          type="button"
                          className={`admin-btn admin-btn-cash ${isPaid && paidMethod === "cash" ? "active" : ""}`}
                          onClick={() => handlePayment(student.id, "cash", paidMethod)}
                          disabled={updatingId !== null || (isPaid && paidMethod === "cash")}
                        >
                          {isCashLoading ? <Spinner size="sm" animation="border" /> : (isPaid && paidMethod === "cash" ? "Cash Selected" : "Paid via Cash")}
                        </button>
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>
    </div>
  );
}

/* ---------------- Internal CSS ---------------- */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.admin-page {
  --bg: #07080b;
  --surface: #0d0f14;
  --surface-2: #11141b;
  --line: #1c212c;
  --line-2: #2a3140;
  --text: #ffffff;
  --body: #b4bac7;
  --muted: #8a93a6;
  --blue: #2563eb;
  --blue-2: #3b82f6;
  --green: #22c55e;
  --green-dark: #16a34a;
  --amber: #fbbf24;
  --bad: #f87171;
  min-height: 100vh;
  background-color: var(--bg);
  color: var(--body);
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  padding-bottom: 50px;
}

/* ════ LOGIN SCREEN CSS ════ */
.admin-login-box {
  background: var(--surface);
  border: 1px solid var(--line-2);
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
}
.admin-login-icon {
  width: 64px;
  height: 64px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: var(--blue-2);
}
.admin-login-box h2 {
  color: var(--text);
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 8px;
}
.admin-login-box p {
  color: var(--muted);
  font-size: 0.9rem;
  margin-bottom: 24px;
  line-height: 1.5;
}

/* New Password Wrapper with Eye Button */
.admin-password-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.admin-password-wrapper input {
  width: 100%;
  background: var(--surface-2);
  border: 1px solid var(--line-2);
  border-radius: 10px;
  padding: 14px 44px 14px 16px; /* Extra right padding for the eye icon */
  color: var(--text);
  font-size: 1rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s;
}
.admin-password-wrapper input:focus {
  border-color: var(--blue-2);
}
.admin-password-wrapper input::placeholder {
  color: #5d6579;
}
.admin-eye-btn {
  position: absolute;
  right: 12px;
  background: transparent;
  border: none;
  color: var(--muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  transition: color 0.2s;
}
.admin-eye-btn:hover {
  color: var(--text);
}

/* ════ DASHBOARD CSS ════ */
.admin-header {
  background: rgba(7, 8, 11, 0.85);
  border-bottom: 1px solid var(--line);
  padding: 20px 0 0;
  backdrop-filter: blur(8px);
  margin-bottom: 30px;
}

.admin-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.admin-header h1 {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--text);
  margin: 0 0 4px;
  letter-spacing: -0.02em;
}

.admin-header p {
  margin: 0;
  color: var(--muted);
  font-size: 0.9rem;
}

.admin-header-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.admin-action-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}

.admin-compact-stats {
  display: flex;
  align-items: center;
  gap: 16px;
  background: var(--surface);
  border: 1px solid var(--line-2);
  padding: 8px 16px;
  border-radius: 10px;
}

.admin-stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
}

.admin-stat-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.upi-dot { background: #60a5fa; box-shadow: 0 0 8px rgba(96, 165, 250, 0.4); }
.cash-dot { background: #4ade80; box-shadow: 0 0 8px rgba(74, 222, 128, 0.4); }

.admin-stat-text { color: var(--body); }
.admin-stat-text strong { color: var(--text); margin-left: 4px; }
.admin-stat-count { color: var(--muted); font-size: 0.75rem; margin-left: 2px; }

.admin-tabs-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 20px;
  width: 100%;
}

.admin-tab {
  background: var(--surface);
  border: 1px solid var(--line-2);
  color: var(--muted);
  font-family: inherit;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 8px 16px;
  cursor: pointer;
  white-space: nowrap;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.admin-tab:hover { 
  color: var(--text); 
  border-color: var(--line);
  background: var(--surface-2);
}

.admin-tab.active {
  color: #fff;
  background: var(--blue);
  border-color: var(--blue-2);
  box-shadow: 0 0 15px rgba(37, 99, 235, 0.3);
}

.admin-tab-count {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--muted);
  background: var(--surface-2);
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 8px;
  transition: all 0.2s ease;
}

.admin-tab.active .admin-tab-count {
  color: var(--blue-2);
  background: #fff;
}

.admin-search-box {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--surface);
  border: 1px solid var(--line-2);
  border-radius: 14px;
  padding: 0 16px;
  margin-bottom: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: border-color 0.2s, box-shadow 0.2s;
}
.admin-search-box:focus-within {
  border-color: var(--blue-2);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
}
.admin-search-icon { color: var(--muted); margin-right: 12px; }
.admin-search-input {
  flex: 1;
  background: transparent;
  border: 0;
  padding: 16px 0;
  font-size: 1rem;
  color: var(--text);
  font-family: inherit;
  outline: none;
}
.admin-search-input::placeholder { color: #5d6579; }

.admin-loading, .admin-empty {
  text-align: center;
  padding: 60px 20px;
  color: var(--muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.admin-empty h3 { color: var(--text); margin: 0; }

.admin-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 18px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  transition: transform 0.2s, box-shadow 0.2s;
}
.admin-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(59, 130, 246, 0.2);
}
.admin-card-top { display: flex; gap: 16px; padding: 20px; }
.admin-photo {
  width: 80px;
  height: 100px;
  flex-shrink: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid var(--line-2);
  background: var(--surface-2);
  position: relative;
  transition: border-color 0.3s;
}
.admin-photo:hover { border-color: var(--blue-2); }
.admin-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.admin-img { animation: fadeIn 0.5s ease-in-out; opacity: 0; animation-fill-mode: forwards; }
@keyframes fadeIn { to { opacity: 1; } }

.admin-card-basic { flex: 1; min-width: 0; }
.admin-card-basic h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.admin-ref {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--blue-2);
  background: rgba(59, 130, 246, 0.1);
  padding: 2px 8px;
  border-radius: 6px;
  margin-bottom: 10px;
}
.admin-info-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--muted);
  margin-bottom: 4px;
}
.admin-info-row span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.admin-info-row svg { flex-shrink: 0; }

.admin-divider { height: 1px; background: var(--line); margin: 0 20px; }

.admin-course-details {
  padding: 16px 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.admin-detail-row { display: flex; flex-direction: column; gap: 2px; }
.admin-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.admin-value { font-size: 0.85rem; font-weight: 600; color: var(--text); }
.admin-stream-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.admin-payment-section {
  margin-top: auto;
  padding: 16px 20px 20px;
  background: var(--surface-2);
  border-top: 1px solid var(--line);
}
.admin-payment-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--amber);
  margin-bottom: 12px;
}

.admin-paid-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--green);
  font-weight: 700;
  font-size: 0.85rem;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px dashed rgba(34, 197, 94, 0.3);
}

.admin-payment-actions { display: flex; gap: 10px; }

.admin-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 8px;
  padding: 8px 14px;
  font-weight: 600;
  font-size: 0.85rem;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid transparent;
  flex: 1;
}
.admin-btn:disabled { opacity: 0.7; cursor: not-allowed; }

.admin-btn-upi {
  background: rgba(37, 99, 235, 0.1);
  color: #60a5fa;
  border-color: rgba(37, 99, 235, 0.3);
}
.admin-btn-upi:hover:not(:disabled) { background: var(--blue); color: #fff; }
.admin-btn-upi.active {
  background: var(--blue);
  color: #fff;
  border-color: var(--blue);
  box-shadow: 0 0 10px rgba(37, 99, 235, 0.4);
}

.admin-btn-cash {
  background: rgba(34, 197, 94, 0.1);
  color: #4ade80;
  border-color: rgba(34, 197, 94, 0.3);
}
.admin-btn-cash:hover:not(:disabled) { background: var(--green-dark); color: #fff; }
.admin-btn-cash.active {
  background: var(--green-dark);
  color: #fff;
  border-color: var(--green-dark);
  box-shadow: 0 0 10px rgba(34, 197, 94, 0.4);
}

.admin-btn-outline {
  background: transparent;
  color: var(--text);
  border: 1px solid var(--line-2);
  flex: none;
}
.admin-btn-outline:hover:not(:disabled) { border-color: var(--blue-2); background: rgba(59, 130, 246, 0.08); }

.admin-btn-primary {
  background: var(--blue);
  color: #fff;
  border: 1px solid var(--blue);
  flex: none;
}
.admin-btn-primary:hover:not(:disabled) { background: var(--blue-2); border-color: var(--blue-2); }

.admin-btn-logout {
  background: rgba(248, 113, 113, 0.1);
  color: var(--bad);
  border: 1px solid rgba(248, 113, 113, 0.3);
  flex: none;
}
.admin-btn-logout:hover:not(:disabled) { background: var(--bad); color: #fff; }

.spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* Mobile responsive */
@media (max-width: 767px) {
  .admin-header-actions {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .admin-compact-stats {
    flex: 1;
    justify-content: space-around;
  }
}
`;